import { collection, addDoc, getDocs, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import Papa from 'papaparse';

const calculateDayOfYear = (dateStr) => {
  try {
    const [month, day, year] = dateStr.split('/').map(num => parseInt(num));
    const date = new Date(year, month - 1, day);
    const startOfYear = new Date(2025, 0, 1);
    const diffTime = date - startOfYear;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  } catch (error) {
    console.error('Error calculating day of year for date:', dateStr, error);
    return null;
  }
};

// Helper function to get content with fallback
const getContentWithFallback = (row, primaryKey, fallbackKey) => {
  const primary = row[primaryKey]?.trim() || '';
  const fallback = row[fallbackKey]?.trim() || '';
  return primary || fallback;
};

export const migrateCSVToFirestore = async (csvData) => {
  try {
    const devotionalsRef = collection(db, 'devotionals');
    
    // Parse CSV data with specific column mapping
    const parsedData = Papa.parse(csvData, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim()
    });

    console.log('Total rows in CSV:', parsedData.data.length);
    console.log('Sample row:', parsedData.data[0]);

    const batch = [];
    const skippedRows = [];
    
    // Transform and prepare data for Firestore
    parsedData.data.forEach((row, index) => {
      // Calculate the day of year from the Date to Display
      const dayOfYear = calculateDayOfYear(row['Date to Display']);
      
      // Map columns according to specification
      const devotional = {
        day: dayOfYear,
        chapter: parseInt(row['Chapter Number']) || 1,  // Column D (D38)
        verse: parseInt(row['Verse Number']) || 1,      // Column E (E38)
        scriptureReference: row['Scripture Reference']?.trim() || '', // Column F (F38)
        // Title from H38, fallback to G38
        title: getContentWithFallback(row, 'Custom Devo Title Created', 'Devo Title'),
        // Devotional content from J38, fallback to I38
        devotionalContent: getContentWithFallback(row, 'Devotionals Produced', 'Daily Devotionals'),
        // Prayer from N38, fallback to M38 (this one works correctly)
        prayer: getContentWithFallback(row, 'Custom Prayer Created', 'Short Prayer'),
        // Daily Challenge from P38, fallback to O38
        dailyChallenge: getContentWithFallback(row, 'Custom Challenge Created', 'Daily Challenge'),
        // Additional metadata
        lastModified: new Date().toISOString(),
        author: 'system_migration',
        originalDate: row['Date to Display']
      };

      // Log the mapping for verification
      console.log(`Processing row for date ${row['Date to Display']}:`, {
        title: {
          primary: row['Custom Devo Title Created'],
          fallback: row['Devo Title'],
          final: devotional.title
        },
        content: {
          primary: row['Devotionals Produced'],
          fallback: row['Daily Devotionals'],
          final: devotional.devotionalContent
        },
        prayer: {
          primary: row['Custom Prayer Created'],
          fallback: row['Short Prayer'],
          final: devotional.prayer
        },
        challenge: {
          primary: row['Custom Challenge Created'],
          fallback: row['Daily Challenge'],
          final: devotional.dailyChallenge
        }
      });

      // Validate required fields
      if (!dayOfYear || !devotional.scriptureReference || !devotional.devotionalContent || !devotional.prayer) {
        skippedRows.push({
          index: index + 1,
          reason: {
            dayOfYear: !dayOfYear,
            scripture: !devotional.scriptureReference,
            content: !devotional.devotionalContent,
            prayer: !devotional.prayer
          },
          date: row['Date to Display']
        });
        return; // Skip this row
      }

      batch.push(devotional);
    });

    if (batch.length === 0) {
      throw new Error('No valid devotionals found in CSV data');
    }

    console.log(`Preparing to migrate ${batch.length} valid devotionals...`);
    console.log('Skipped rows:', skippedRows);
    console.log('First devotional to be migrated:', batch[0]);
    console.log('Last devotional to be migrated:', batch[batch.length - 1]);

    // Upload to Firestore
    const results = await Promise.all(
      batch.map(doc => addDoc(devotionalsRef, doc))
    );

    console.log(`Successfully migrated ${results.length} devotionals to Firestore`);
    return {
      success: true,
      count: results.length,
      skippedCount: skippedRows.length,
      skippedDetails: skippedRows
    };
  } catch (error) {
    console.error('Error during migration:', error);
    throw error;
  }
};

export const validateMigration = async () => {
  try {
    const devotionalsRef = collection(db, 'devotionals');
    const q = query(devotionalsRef);
    const snapshot = await getDocs(q);
    return snapshot.size; // Returns number of documents
  } catch (error) {
    console.error('Error validating migration:', error);
    throw error;
  }
}; 